import { useEffect, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { KPICard } from "@/components/nadia/KPICard";
import { Badge } from "@/components/ui/badge";
import { OptimizerPanel } from "@/components/nadia/OptimizerPanel";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

import { Wallet, MousePointerClick, Users, Eye, Target, TrendingUp } from "lucide-react";

const metric = (value, prefix = "", suffix = "") => value == null ? "—" : `${prefix}${Number(value).toLocaleString("it-IT")}${suffix}`;

const QUALITY_PRESENTATION = new Map([
  ["mancante", { label: "Dato mancante", style: "border-slate-500/30 bg-slate-500/10 text-slate-300" }],
  ["non_verificabile", { label: "Non verificabile", style: "border-amber-500/30 bg-amber-500/10 text-amber-200" }],
  ["verificabile", { label: "Dato verificabile", style: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" }],
  ["obsoleto", { label: "Dato obsoleto", style: "border-orange-500/30 bg-orange-500/10 text-orange-200" }],
  ["simulato", { label: "Dato simulato", style: "border-rose-500/30 bg-rose-500/10 text-rose-200" }],
]);

const DataQualityMetadata = ({ scope, label, quality, available }) => {
  const hasQuality = typeof quality === "string" && quality.trim().length > 0;
  const hasAvailability = typeof available === "boolean";
  if (!hasQuality && !hasAvailability) return null;
  const presentation = QUALITY_PRESENTATION.get(quality);
  // Mostra solo i metadati ricevuti: nessuna qualita' dedotta dai numeri o dalla disponibilita'.
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid={`results-${scope}-metadata`}>
      <span className="text-xs text-slate-400" data-testid={`results-${scope}-metadata-label`}>{label}</span>
      {hasQuality && (
        <Badge variant="outline" className={presentation?.style || "border-slate-500/30 text-slate-300"} data-testid={`results-${scope}-quality`}>
          {presentation?.label || quality}
        </Badge>
      )}
      {hasAvailability && (
        <span className="text-xs text-slate-300" data-testid={`results-${scope}-available`}>
          {available ? "Dati presenti" : "Dati non disponibili"}
        </span>
      )}
    </div>
  );
};

export default function Risultati() {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = () => {
    setLoading(true);
    setError(false);
    api.get("/campaigns", { timeout: 15000 })
      .then(({ data }) => setC(data[0] || null))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const m = c?.metrics || {};
  const hasMetrics = Object.values(m).some((v) => typeof v === "number");

  return (
    <>
      <PageHeader
        eyebrow="Risultati"
        title="Come sta andando davvero"
        subtitle="Metriche delle campagne, con indicazioni di qualità e disponibilità quando fornite."
      />

      {!loading && !error && c && <>
        <DataQualityMetadata scope="metrics" label="Metriche" quality={c.metrics_quality} available={c.metrics_available} />
        <DataQualityMetadata scope="data" label="Riepilogo" quality={c.data_quality} available={c.data_available} />
      </>}

      {loading ? <Skeleton className="h-96 bg-white/5" data-testid="results-loading" /> : error ? (
        <div className="nadia-card p-6 space-y-3" role="alert" data-testid="results-error">
          <p className="text-sm text-slate-300">Non riesco a caricare i risultati. Riprova tra poco.</p>
          <Button onClick={load} variant="outline" data-testid="results-retry">Riprova</Button>
        </div>
      ) : !c || !hasMetrics ? (
        <div className="nadia-card p-6 text-sm text-slate-300" data-testid="results-empty">
          Non ci sono ancora risultati disponibili. Qui vedrai le metriche quando saranno disponibili dati delle tue campagne.
        </div>
      ) : <>
      {/* KPI originali: la qualita' non modifica valori o visibilita'. */}
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-heading text-lg font-semibold text-slate-100" data-testid="results-metrics-heading">Metriche</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
        <KPICard testId="r-spend" label="Spesa" value={metric(c.spend, "€")} icon={Wallet} />
        <KPICard testId="r-impressions" label="Impressioni" value={metric(m.impressions)} icon={Eye} />
        <KPICard testId="r-reach" label="Persone raggiunte" value={metric(m.reach)} icon={Users} />
        <KPICard testId="r-clicks" label="Clic" value={metric(m.clicks)} icon={MousePointerClick} />
        <KPICard testId="r-ctr" label="Tasso di clic" value={metric(m.ctr, "", "%")} helper="CTR" />
        <KPICard testId="r-cpc" label="Costo per clic" value={metric(m.cpc, "€")} helper="CPC" />
        <KPICard testId="r-conv" label="Conversioni" value={metric(m.conversions)} icon={Target} />
        <KPICard testId="r-cpa" label="Costo per conversione" value={metric(m.cpa, "€")} helper="CPA" />
        <KPICard testId="r-revenue" label="Ricavi" value={metric(m.revenue, "€")} icon={TrendingUp} />
        <KPICard testId="r-roas" label="ROAS" value={metric(m.roas, "", "x")} icon={TrendingUp} />
      </div>
      </>}

      {!loading && !error && <OptimizerPanel />}
    </>
  );
}
